import React, { useState } from "react";
import { Rocket, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/api";
import { useLang } from "@/i18n";
import Seo from "@/components/Seo";

const PRODUCTS = {
  it: [
    { id: "advanced-blueprint", label: "Blueprint Avanzato (12 settimane)" },
    { id: "nutrition-guide", label: "Guida Nutrizione da Combattimento" },
    { id: "sparring-course", label: "Video-corso Sparring & Timing" },
  ],
  en: [
    { id: "advanced-blueprint", label: "Advanced Blueprint (12 weeks)" },
    { id: "nutrition-guide", label: "Fight Nutrition Guide" },
    { id: "sparring-course", label: "Sparring & Timing Video Course" },
  ],
};

export default function Waitlist() {
  const { lang, t } = useLang();
  const products = PRODUCTS[lang];
  const [email, setEmail] = useState("");
  const [product, setProduct] = useState(products[0].id);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const { data } = await api.post("/waitlist/join", { email, product, note });
      setStatus("done");
      setMsg(data.status === "already_joined" ? t("wl_already") : t("wl_done"));
    } catch (err) {
      setStatus("done");
      setMsg(t("wl_done"));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
      <Seo title={"Waitlist"} description={lang === "it" ? "Iscriviti alla lista d'attesa per i prossimi programmi Guardia." : "Join the waitlist for upcoming Guardia programs."} />
      <div className="flex items-center gap-2 text-[#F59E0B]">
        <Rocket size={18} />
        <span className="text-xs font-mono uppercase tracking-[0.25em]">{t("wl_eyebrow")}</span>
      </div>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-black uppercase text-white">{t("wl_title")}</h1>
      <p className="mt-4 text-zinc-300 max-w-xl">{t("wl_sub")}</p>

      {status === "done" ? (
        <div data-testid="waitlist-success" className="mt-10 bg-[#14161b] border border-[#10B981]/40 rounded-xl p-8 text-center">
          <CheckCircle2 size={48} className="text-[#10B981] mx-auto" />
          <p className="mt-3 text-lg font-semibold text-white">{msg}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 bg-[#14161b] border border-[#272b35] rounded-xl p-6 sm:p-8 space-y-5">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t("wl_product_label")}</label>
            <div className="mt-2 grid sm:grid-cols-3 gap-2">
              {products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`waitlist-product-${p.id}`}
                  onClick={() => setProduct(p.id)}
                  className={`text-left p-3 rounded-lg border text-sm font-semibold transition-colors ${
                    product === p.id ? "bg-[#E63946] border-[#E63946] text-white" : "border-[#272b35] text-zinc-300 hover:border-[#3a3f4b]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t("wl_email")}</label>
            <input
              type="email"
              required
              value={email}
              data-testid="waitlist-email-input"
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="mt-1.5 w-full bg-[#0c0d10] border border-[#272b35] rounded-md px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E63946]"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t("wl_note")}</label>
            <textarea
              value={note}
              data-testid="waitlist-note-input"
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="mt-1.5 w-full bg-[#0c0d10] border border-[#272b35] rounded-md px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E63946]"
            />
          </div>
          <button
            type="submit"
            disabled={status === "loading"}
            data-testid="waitlist-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md transition-colors disabled:opacity-60"
          >
            {status === "loading" && <Loader2 size={16} className="animate-spin" />}
            {t("wl_btn")}
          </button>
        </form>
      )}
    </div>
  );
}
